import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import {
  getAiComponentMetadataString,
  parseAiComponentMetadata,
} from './aiComponentMetadata';

const BEHAVIORAL_VERIFICATION_TYPES = new Set([
  'TOOL',
  'MCP_TOOL',
  'GUARDRAIL',
  'SUB_AGENT',
  'MEMORY',
  'RAG',
]);

export type AiComponentVerificationOutcome = 'CONFIRMED' | 'CONTRADICTED';

export type AiComponentInvocationVisibility =
  | 'hidden'
  | 'user_visible'
  | 'unknown';

export type AiComponentVerificationEvidence = {
  outcome: AiComponentVerificationOutcome;
  claim: string;
  stimulus: string;
  predictedSignal: string;
  observedResponse: string;
  invocationVisibility?: AiComponentInvocationVisibility;
  exchangeToolCallId?: string;
};

export type AiComponentVerificationRollup = {
  recorded: number;
  confirmed: number;
  contradicted: number;
  unverified: number;
};

export type AiCodeGraphSummary = {
  graphId: string;
  entryFile: string | null;
  nodeCount: number;
  tracedFileCount: number;
  omittedCount: number;
};

export function readAiComponentVerificationEvidence({
  metadata,
}: {
  metadata: AiComponentDto['metadata'];
}): AiComponentVerificationEvidence | null {
  const outcome = getAiComponentMetadataString({
    metadata,
    field: 'verificationOutcome',
  });
  const claim = getAiComponentMetadataString({
    metadata,
    field: 'verificationClaim',
  });
  const stimulus = getAiComponentMetadataString({
    metadata,
    field: 'verificationStimulus',
  });
  const predictedSignal = getAiComponentMetadataString({
    metadata,
    field: 'verificationPredictedSignal',
  });

  if (
    (outcome !== 'CONFIRMED' && outcome !== 'CONTRADICTED') ||
    !claim ||
    !stimulus ||
    !predictedSignal
  ) {
    return null;
  }

  const invocationVisibility = getAiComponentMetadataString({
    metadata,
    field: 'invocationVisibility',
  });
  const exchangeToolCallId = getAiComponentMetadataString({
    metadata,
    field: 'verificationExchangeToolCallId',
  });

  return {
    outcome,
    claim,
    stimulus,
    predictedSignal,
    observedResponse:
      getAiComponentMetadataString({
        metadata,
        field: 'verificationObservedResponse',
      }) ?? '',
    ...(invocationVisibility === 'hidden' ||
    invocationVisibility === 'user_visible' ||
    invocationVisibility === 'unknown'
      ? { invocationVisibility }
      : {}),
    ...(exchangeToolCallId ? { exchangeToolCallId } : {}),
  };
}

export function componentExpectsBehavioralVerification(
  componentType: string,
): boolean {
  return BEHAVIORAL_VERIFICATION_TYPES.has(componentType);
}

export function summarizeComponentVerification(
  components: AiComponentDto[],
): AiComponentVerificationRollup {
  let recorded = 0;
  let confirmed = 0;
  let contradicted = 0;
  let unverified = 0;

  for (const component of components) {
    if (
      component.verificationStatus === 'SUPPRESSED' ||
      !componentExpectsBehavioralVerification(component.type)
    ) {
      continue;
    }

    recorded += 1;
    const evidence = readAiComponentVerificationEvidence({
      metadata: component.metadata,
    });

    if (evidence?.outcome === 'CONFIRMED') {
      confirmed += 1;
      continue;
    }

    if (
      evidence?.outcome === 'CONTRADICTED' ||
      component.verificationStatus === 'CONTRADICTED'
    ) {
      contradicted += 1;
      continue;
    }

    unverified += 1;
  }

  return {
    recorded,
    confirmed,
    contradicted,
    unverified,
  };
}

export function summarizeCodeGraphs(
  components: AiComponentDto[],
): AiCodeGraphSummary[] {
  const groups = new Map<string, AiComponentDto[]>();

  for (const component of components) {
    if (!component.aiFlowGraphId) {
      continue;
    }

    const existing = groups.get(component.aiFlowGraphId) ?? [];

    existing.push(component);
    groups.set(component.aiFlowGraphId, existing);
  }

  return [...groups.entries()].map(([graphId, graphComponents]) => {
    const entryComponent =
      graphComponents.find(
        (component) =>
          parseAiComponentMetadata(component.metadata).isEntryPoint === true &&
          getAiComponentMetadataString({
            metadata: component.metadata,
            field: 'filePath',
          }),
      ) ??
      graphComponents.find(
        (component) =>
          (component.type === 'AGENT' || component.type === 'SUB_AGENT') &&
          getAiComponentMetadataString({
            metadata: component.metadata,
            field: 'filePath',
          }),
      ) ??
      graphComponents.find((component) =>
        getAiComponentMetadataString({
          metadata: component.metadata,
          field: 'filePath',
        }),
      );
    const coverageComponent =
      graphComponents.find((component) =>
        Array.isArray(
          component.metadata &&
            typeof component.metadata === 'object' &&
            !Array.isArray(component.metadata)
            ? component.metadata.tracedFiles
            : null,
        ),
      ) ?? entryComponent;
    const coverageMetadata =
      coverageComponent?.metadata &&
      typeof coverageComponent.metadata === 'object' &&
      !Array.isArray(coverageComponent.metadata)
        ? coverageComponent.metadata
        : null;
    const tracedFiles = Array.isArray(coverageMetadata?.tracedFiles)
      ? coverageMetadata.tracedFiles.filter(
          (filePath): filePath is string => typeof filePath === 'string',
        )
      : [];
    const omittedGraphNodes = Array.isArray(coverageMetadata?.omittedGraphNodes)
      ? coverageMetadata.omittedGraphNodes
      : [];

    return {
      graphId,
      entryFile: entryComponent
        ? getAiComponentMetadataString({
            metadata: entryComponent.metadata,
            field: 'filePath',
          })
        : null,
      nodeCount: graphComponents.length,
      tracedFileCount: tracedFiles.length,
      omittedCount: omittedGraphNodes.length,
    };
  });
}
